/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { FormControlOption, SelectFieldOption } from '@squiz/sds';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EventSchedulerType } from '../../../../Services/event-bus';
import { EVENT_SUBSCRIPTION_BUILDER_DATA_IDS } from './EventSubscriptionBuilderForm.constants';
import {
  AttributeRule,
  EventAdvancedFormsFormResponseCreateAttributes,
  EventDatastoreBlueprintChangeAttributes,
  EventDatastoreDocumentChangeAttributes,
  EventSubscriptionEventType,
  EventSubscriptionSource,
  EventSubscriptionTargetType,
} from './EventSubscriptionBuilderForm.defs';

/**
 * All possible event subscription source options.
 */
export const SUBSCRIPTION_SOURCE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: `advancedforms`,
    label: `Advanced forms`,
    value: `advancedforms`,
  },
  {
    key: `datastore`,
    label: `Datastore`,
    value: `datastore`,
  },
  {
    key: `event-scheduler`,
    label: `Event scheduler`,
    value: `event-scheduler`,
  },
  {
    key: `matrix`,
    label: `Matrix`,
    value: `matrix`,
  },
];

/**
 * All possible event type options mapped to its corresponding event subscription source.
 */
export const EVENT_TYPE_OPTIONS: Record<EventSubscriptionSource, Array<SelectFieldOption>> = {
  [EventSubscriptionSource.AdvancedForms]: [
    {
      key: EventSubscriptionEventType.FormResponseCreate,
      label: EventSubscriptionEventType.FormResponseCreate,
      value: EventSubscriptionEventType.FormResponseCreate,
    },
  ],
  [EventSubscriptionSource.Datastore]: [
    {
      key: EventSubscriptionEventType.DocumentChangeCreate,
      label: EventSubscriptionEventType.DocumentChangeCreate,
      value: EventSubscriptionEventType.DocumentChangeCreate,
    },
    {
      key: EventSubscriptionEventType.DocumentChangeUpdate,
      label: EventSubscriptionEventType.DocumentChangeUpdate,
      value: EventSubscriptionEventType.DocumentChangeUpdate,
    },
    {
      key: EventSubscriptionEventType.DocumentChangeDelete,
      label: EventSubscriptionEventType.DocumentChangeDelete,
      value: EventSubscriptionEventType.DocumentChangeDelete,
    },
    {
      key: EventSubscriptionEventType.BlueprintChangeCreate,
      label: EventSubscriptionEventType.BlueprintChangeCreate,
      value: EventSubscriptionEventType.BlueprintChangeCreate,
    },
    {
      key: EventSubscriptionEventType.BlueprintChangeUpdate,
      label: EventSubscriptionEventType.BlueprintChangeUpdate,
      value: EventSubscriptionEventType.BlueprintChangeUpdate,
    },
  ],
  [EventSubscriptionSource.Matrix]: [
    {
      key: EventSubscriptionEventType.AssetChangeCreate,
      label: EventSubscriptionEventType.AssetChangeCreate,
      value: EventSubscriptionEventType.AssetChangeCreate,
    },
    {
      key: EventSubscriptionEventType.AssetChangeUpdate,
      label: EventSubscriptionEventType.AssetChangeUpdate,
      value: EventSubscriptionEventType.AssetChangeUpdate,
    },
    {
      key: EventSubscriptionEventType.AssetChangeDelete,
      label: EventSubscriptionEventType.AssetChangeDelete,
      value: EventSubscriptionEventType.AssetChangeDelete,
    },
  ],
};

/**
 * All possible event target type options.
 */
export const EVENT_SUBSCRIPTION_TARGET_TYPE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: EventSubscriptionTargetType.Api,
    label: CONFIGURATION_STRINGS.apiTargetType,
    value: EventSubscriptionTargetType.Api,
  },
  {
    key: EventSubscriptionTargetType.IntegrationsFlow,
    label: CONFIGURATION_STRINGS.integrationsFlowTargetType,
    value: EventSubscriptionTargetType.IntegrationsFlow,
  },
  {
    key: EventSubscriptionTargetType.Job,
    label: CONFIGURATION_STRINGS.jobTargetType,
    value: EventSubscriptionTargetType.Job,
  },
];

/**
 * Target type options available to the event scheduler source.
 */
export const EVENT_SUBSCRIPTION_TARGET_TYPE_SCHEDULER_OPTIONS: Array<SelectFieldOption> =
  EVENT_SUBSCRIPTION_TARGET_TYPE_OPTIONS.filter((option) => option.value !== EventSubscriptionTargetType.Api);

/**
 * All possible attribute options corresponding to the advanced forms 'form.response.create' event type.
 */
const EVENT_ADVANCED_FORMS_FORM_RESPONSE_CREATE_ATTRIBUTE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: EventAdvancedFormsFormResponseCreateAttributes.formId,
    label: `formId`,
    value: EventAdvancedFormsFormResponseCreateAttributes.formId,
  },
];

/**
 * All possible attribute options corresponding to the datastore 'document.change.*' event types.
 */
const EVENT_DATASTORE_DOCUMENT_CHANGE_ATTRIBUTE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: EventDatastoreDocumentChangeAttributes.InstanceId,
    label: EventDatastoreDocumentChangeAttributes.InstanceId,
    value: EventDatastoreDocumentChangeAttributes.InstanceId,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.ServiceUrl,
    label: EventDatastoreDocumentChangeAttributes.ServiceUrl,
    value: EventDatastoreDocumentChangeAttributes.ServiceUrl,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.ChangedValues,
    label: EventDatastoreDocumentChangeAttributes.ChangedValues,
    value: EventDatastoreDocumentChangeAttributes.ChangedValues,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.Inherited,
    label: EventDatastoreDocumentChangeAttributes.Inherited,
    value: EventDatastoreDocumentChangeAttributes.Inherited,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.Origin,
    label: EventDatastoreDocumentChangeAttributes.Origin,
    value: EventDatastoreDocumentChangeAttributes.Origin,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.Collection,
    label: EventDatastoreDocumentChangeAttributes.Collection,
    value: EventDatastoreDocumentChangeAttributes.Collection,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.DocumentId,
    label: EventDatastoreDocumentChangeAttributes.DocumentId,
    value: EventDatastoreDocumentChangeAttributes.DocumentId,
  },
  {
    key: EventDatastoreDocumentChangeAttributes.DocumentPath,
    label: EventDatastoreDocumentChangeAttributes.DocumentPath,
    value: EventDatastoreDocumentChangeAttributes.DocumentPath,
  },
];

/**
 * All possible attribute options corresponding to the datastore 'blueprint.change.*' event types.
 */
const EVENT_DATASTORE_BLUEPRINT_CHANGE_ATTRIBUTE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: EventDatastoreBlueprintChangeAttributes.InstanceId,
    label: EventDatastoreBlueprintChangeAttributes.InstanceId,
    value: EventDatastoreBlueprintChangeAttributes.InstanceId,
  },
  {
    key: EventDatastoreBlueprintChangeAttributes.Origin,
    label: EventDatastoreBlueprintChangeAttributes.Origin,
    value: EventDatastoreBlueprintChangeAttributes.Origin,
  },
  {
    key: EventDatastoreBlueprintChangeAttributes.ServiceUrl,
    label: EventDatastoreBlueprintChangeAttributes.ServiceUrl,
    value: EventDatastoreBlueprintChangeAttributes.ServiceUrl,
  },
  {
    key: EventDatastoreBlueprintChangeAttributes.Version,
    label: EventDatastoreBlueprintChangeAttributes.Version,
    value: EventDatastoreBlueprintChangeAttributes.Version,
  },
];

/**
 * All possible attribute options corresponding to the matrix 'asset.change.*' event types.
 * Currently hardcoded fake options for matrix.
 */
const EVENT_MATRIX_ASSET_CHANGE_ATTRIBUTE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: `id`,
    label: `id`,
    value: `id`,
  },
  {
    key: `name`,
    label: `name`,
    value: `name`,
  },
];

/**
 * All possible attribute options corresponding to the datastore 'document.change.*' event types.
 */
export const EVENT_ATTRIBUTE_FILTER_OPTIONS: Record<EventSubscriptionEventType, Array<SelectFieldOption>> = {
  [EventSubscriptionEventType.AssetChangeCreate]: EVENT_MATRIX_ASSET_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.AssetChangeDelete]: EVENT_MATRIX_ASSET_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.AssetChangeUpdate]: EVENT_MATRIX_ASSET_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.BlueprintChangeCreate]: EVENT_DATASTORE_BLUEPRINT_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.BlueprintChangeUpdate]: EVENT_DATASTORE_BLUEPRINT_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.DocumentChangeCreate]: EVENT_DATASTORE_DOCUMENT_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.DocumentChangeDelete]: EVENT_DATASTORE_DOCUMENT_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.DocumentChangeUpdate]: EVENT_DATASTORE_DOCUMENT_CHANGE_ATTRIBUTE_OPTIONS,
  [EventSubscriptionEventType.FormResponseCreate]: EVENT_ADVANCED_FORMS_FORM_RESPONSE_CREATE_ATTRIBUTE_OPTIONS,
};

/**
 * All possible attribute filter rule options.
 */
export const EVENT_ATTRIBUTE_FILTER_RULE_OPTIONS: Array<SelectFieldOption> = [
  {
    key: `equals`,
    label: `Equals`,
    value: AttributeRule.Equals,
  },
  {
    key: `not-equals`,
    label: `Does not equal`,
    value: AttributeRule.NotEquals,
  },
  {
    key: `less-than`,
    label: `Less than`,
    value: AttributeRule.LessThan,
  },
  {
    key: `less-than-equals`,
    label: `Less than or equal`,
    value: AttributeRule.LessThanEquals,
  },
  {
    key: `greater-than`,
    label: `Greater than`,
    value: AttributeRule.GreaterThan,
  },
  {
    key: `greater-than-equals`,
    label: `Greater than or equal`,
    value: AttributeRule.GreaterThanEquals,
  },
  {
    key: `begins-with`,
    label: `Begins with`,
    value: AttributeRule.BeginsWith,
  },
  {
    key: `exists`,
    label: `Exists`,
    value: AttributeRule.Exists,
  },
  {
    key: `not-exists`,
    label: `Does not exist`,
    value: AttributeRule.NotExists,
  },
  {
    key: `is-null`,
    label: `Is null`,
    value: AttributeRule.IsNull,
  },
  {
    key: `is-empty`,
    label: `Is empty`,
    value: AttributeRule.IsEmpty,
  },
];

export const DEFAULT_EVENT_TRANSFORMER_EVENT_PATHS_MAP = {
  action: '$.detail.metadata.action',
  event: '$.detail.event',
  eventTime: '$.detail.metadata.eventTime',
  source: '$.source',
  tenantID: '$.detail.metadata.tenantID',
};

export const DEFAULT_EVENT_TRANSFORMER_EVENT_TEMPLATE_STRING =
  '{"metadata": {"eventRule":<aws.events.rule-name>,"source":<source>,"action":<action>,"tenantID":<tenantID>,"eventTime":<eventTime>},"event":<event>}';

export const EVENT_SCHEDULER_TYPE_OPTIONS: Array<FormControlOption> = [
  {
    dataId: EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.schedulerOptionOnce,
    key: EventSchedulerType.once,
    label: EventSchedulerType.once[0].toUpperCase() + EventSchedulerType.once.slice(1),
    value: EventSchedulerType.once,
  },
  {
    dataId: EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.schedulerOptionRecurring,
    key: EventSchedulerType.recurring,
    label: EventSchedulerType.recurring[0].toUpperCase() + EventSchedulerType.recurring.slice(1),
    value: EventSchedulerType.recurring,
  },
];
